/**
 * useContentGifts
 *
 * Orchestration hook for the content "gifts received" sheet — the content
 * owner's paginated view of who has gifted / unlocked a piece of content.
 *
 * Mirrors Flutter's content_gifts_bottom_sheet.dart state & flows:
 *   - `_items`        → `gifts`
 *   - `_hasMore`      → `hasMore`
 *   - `_lastFetchedItem` → `lastFetchedRef`
 *   - `getData()`     → `loadInitial` / `loadMore` (early-return when `!hasMore`,
 *                       content_gifts_bottom_sheet.dart:88-90).
 *
 * The pagination shape (state + cursor ref + in-flight guard + mounted ref +
 * mount-time initial load + defensive de-dupe) follows the sibling
 * `useContentComments` hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageCursor } from '@/data/types/json';
import { fetchMediaGiftsUseCase } from '@/domain/usecases/mediaGifts/fetchMediaGiftsUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';

export interface UseContentGiftsResult {
  gifts: AppTransactionEntity[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/** Stable de-dupe key for a gift transaction (the wire has no document id field). */
function giftKey(gift: AppTransactionEntity): string {
  return `${gift.usersInfo.userId}_${gift.createdDate}`;
}

export function useContentGifts(mediaPath: string): UseContentGiftsResult {
  const [gifts, setGifts] = useState<AppTransactionEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const lastFetchedRef = useRef<PageCursor | undefined>(undefined);
  const mountedRef = useRef(true);
  const loadMoreInFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadInitial = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await fetchMediaGiftsUseCase.execute(mediaPath);
      if (!mountedRef.current) return;

      setGifts(prev => {
        const byKey = new Map<string, AppTransactionEntity>();
        for (const g of prev) byKey.set(giftKey(g), g);
        for (const g of result.gifts) if (!byKey.has(giftKey(g))) byKey.set(giftKey(g), g);
        return Array.from(byKey.values());
      });

      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading gifts', 'useContentGifts.loadInitial', { error: error as Error });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [mediaPath]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    // Flutter getData() returns immediately when `!_hasMore`
    // (content_gifts_bottom_sheet.dart:88-90); the in-flight guard prevents a
    // second concurrent page request from a fast scroll.
    if (!hasMore || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;

    setIsLoading(true);

    try {
      const result = await fetchMediaGiftsUseCase.execute(mediaPath, lastFetchedRef.current);
      if (!mountedRef.current) return;

      setGifts(prev => {
        const byKey = new Map<string, AppTransactionEntity>();
        for (const g of prev) byKey.set(giftKey(g), g);
        for (const g of result.gifts) if (!byKey.has(giftKey(g))) byKey.set(giftKey(g), g);
        return Array.from(byKey.values());
      });

      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading more gifts', 'useContentGifts.loadMore', { error: error as Error });
    } finally {
      loadMoreInFlightRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, [hasMore, mediaPath]);

  return { gifts, isLoading, hasMore, loadMore };
}
